import { useQuery, type UseQueryResult, type UseQueryOptions } from "react-query";
import {
  CourseList,
  CourseSearchParams,
  CourseRelatedContentsResponse,
  AllCourses,
  Course,
} from "types/klass";
import {
  getCoursesSearchApi,
  getFreeCoursesApi,
  getCourseRelatedContentsApi,
  getCourse,
} from "./api";
import { klassKeys } from "./queryKeys";
import { AxiosError } from "axios";

// MARK: 강의 검색 훅
export const useCoursesSearch = <TSelected = CourseList[]>(
  params: CourseSearchParams,
  queryOptions?: Omit<UseQueryOptions<CourseList[], AxiosError, TSelected>, "queryFn" | "queryKey">,
): UseQueryResult<TSelected, AxiosError> => {
  return useQuery<CourseList[], AxiosError, TSelected>({
    queryKey: klassKeys.search(params),
    queryFn: () => getCoursesSearchApi(params),
    ...queryOptions,
  });
};

// MARK: 무료 강의 목록 조회 훅
export const useFreeCourses = <TSelected = CourseList[]>(
  limit?: number,
  queryOptions?: Omit<UseQueryOptions<CourseList[], AxiosError, TSelected>, "queryFn" | "queryKey">,
): UseQueryResult<TSelected, AxiosError> => {
  return useQuery<CourseList[], AxiosError, TSelected>({
    queryKey: klassKeys.free(),
    queryFn: () => getFreeCoursesApi(limit),
    cacheTime: 15 * 60 * 1000,
    staleTime: 15 * 60 * 1000,
    ...queryOptions,
  });
};

// MARK: 강의 연관 컨텐츠 id 조회 훅
export const useCourseRelatedContents = <TSelected = CourseRelatedContentsResponse>(
  params: {
    productId: string;
    contentType: "CLASS" | "SEMINAR";
  },
  queryOptions?: Omit<
    UseQueryOptions<CourseRelatedContentsResponse, AxiosError, TSelected>,
    "queryFn" | "queryKey"
  >,
): UseQueryResult<TSelected, AxiosError> => {
  return useQuery<CourseRelatedContentsResponse, AxiosError, TSelected>({
    queryKey: klassKeys.relatedContents(params),
    queryFn: () => getCourseRelatedContentsApi(params),
    enabled: !!params.productId && !!params.contentType,
    ...queryOptions,
  });
};

// MARK: 강의 상품 조회 훅(단일상품)
export const useLectureCourse = <TSelected = Course>(
  params: { productId: string },
  queryOptions?: Omit<UseQueryOptions<AllCourses, AxiosError, TSelected>, "queryFn" | "queryKey">,
): UseQueryResult<TSelected, AxiosError> => {
  return useQuery<AllCourses, AxiosError, TSelected>({
    queryKey: klassKeys.product(params),
    queryFn: () => getCourse(params),
    enabled: !!params.productId,
    select: (data) => data.course as TSelected,
    ...queryOptions,
  });
};

// MARK: 강의 연관 상품 조회 훅
export const useLectureRelatedCourses = <TSelected = Course[]>(
  productIds: string[],
  queryOptions?: Omit<UseQueryOptions<AllCourses[], AxiosError, TSelected>, "queryFn" | "queryKey">,
): UseQueryResult<TSelected, AxiosError> => {
  return useQuery<AllCourses[], AxiosError, TSelected>({
    queryKey: klassKeys.relatedProducts(productIds),
    queryFn: () => Promise.all(productIds.map((productId) => getCourse({ productId }))),
    enabled: productIds?.length > 0,
    select: (data) => data.map((item) => item.course) as TSelected,
    ...queryOptions,
  });
};
